import React, { useState, useEffect } from 'react';
import { Scale, AlertTriangle, CheckCircle, XCircle, Clock, ExternalLink, Filter, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

const ComplianceDashboard = () => {
  const [consents, setConsents] = useState([]);
  const [documents2257, setDocuments2257] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ platform: 'all', status: 'all', performer: '' });
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    fetchComplianceData();
  }, []);

  const fetchComplianceData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch Faphouse consent records
      const faphouseRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/faphouse/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (faphouseRes.ok) {
        const faphouseData = await faphouseRes.json();
        setConsents(faphouseData || []);
        
        // Generate warnings for missing/expired documents
        const warningsList = [];
        faphouseData.forEach(consent => {
          if (!consent.status || consent.status === 'pending') {
            warningsList.push({
              type: 'missing',
              performer: consent.performer_name || 'Unknown',
              platform: 'Faphouse',
              message: 'Consent pending or missing',
              consentId: consent.id
            });
          }
          if (consent.expiry_date && new Date(consent.expiry_date) < new Date()) {
            warningsList.push({
              type: 'expired',
              performer: consent.performer_name || 'Unknown',
              platform: 'Faphouse',
              message: `Consent expired on ${new Date(consent.expiry_date).toLocaleDateString()}`,
              consentId: consent.id
            });
          }
        });
        setWarnings(warningsList);
      }
      
      // TODO Phase 2: Fetch 2257 documents from backend
      // For now, placeholder
      setDocuments2257([]);
      
    } catch (err) {
      console.error('Failed to fetch compliance data:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredConsents = consents.filter(consent => {
    if (filter.platform !== 'all' && consent.platform !== filter.platform) return false;
    if (filter.status !== 'all' && consent.status !== filter.status) return false;
    if (filter.performer && !consent.performer_name?.toLowerCase().includes(filter.performer.toLowerCase())) return false;
    return true;
  });

  const getStatusBadge = (status) => {
    const statusMap = {
      'approved': { icon: CheckCircle, color: 'text-green-400 bg-green-400/10', label: 'Approved' },
      'pending': { icon: Clock, color: 'text-yellow-400 bg-yellow-400/10', label: 'Pending' },
      'rejected': { icon: XCircle, color: 'text-red-400 bg-red-400/10', label: 'Rejected' },
      'expired': { icon: AlertTriangle, color: 'text-orange-400 bg-orange-400/10', label: 'Expired' }
    };
    const config = statusMap[status] || statusMap.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Scale size={32} className="text-purple-400" />
            <div>
              <h1 className="text-3xl font-bold">Compliance Dashboard</h1>
              <p className="text-zinc-400 text-sm">Loading compliance data...</p>
            </div>
          </div>
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Scale size={32} className="text-purple-400" />
            <div>
              <h1 className="text-3xl font-bold">Compliance Dashboard</h1>
              <p className="text-zinc-400 text-sm">Consent records & 2257 documentation (Admin Only)</p>
            </div>
          </div>
          <Link 
            to="/crm/faphouse"
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition"
          >
            <ExternalLink size={16} />
            Manage Faphouse
          </Link>
        </div>

        {/* Warnings Section */}
        {warnings.length > 0 && (
          <div className="mb-8 p-4 bg-orange-500/10 border border-orange-500/20 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={20} className="text-orange-400" />
              <h2 className="text-lg font-semibold text-orange-400">
                {warnings.length} Missing/Expired Document{warnings.length > 1 ? 's' : ''}
              </h2>
            </div>
            <div className="space-y-2">
              {warnings.slice(0, 5).map((warning, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm">
                  <span className="text-orange-400">•</span>
                  <span className="text-zinc-300">
                    <strong>{warning.performer}</strong> ({warning.platform}): {warning.message}
                  </span>
                  {warning.consentId && (
                    <Link 
                      to={`/crm/faphouse`}
                      className="text-purple-400 hover:text-purple-300 ml-auto"
                    >
                      View →
                    </Link>
                  )}
                </div>
              ))}
              {warnings.length > 5 && (
                <p className="text-xs text-zinc-500 mt-2">+ {warnings.length - 5} more warnings</p>
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 p-4 bg-zinc-900 rounded-xl border border-zinc-800">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={16} className="text-zinc-400" />
            <span className="text-sm font-medium text-zinc-400">Filters</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Platform</label>
              <select 
                value={filter.platform}
                onChange={(e) => setFilter({ ...filter, platform: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white focus:outline-none focus:border-purple-500"
              >
                <option value="all">All Platforms</option>
                <option value="Faphouse">Faphouse</option>
                <option value="OnlyFans">OnlyFans</option>
                <option value="JustForFans">JustForFans</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Status</label>
              <select 
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white focus:outline-none focus:border-purple-500"
              >
                <option value="all">All Statuses</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-zinc-400 mb-1">Search Performer</label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="text"
                  value={filter.performer}
                  onChange={(e) => setFilter({ ...filter, performer: e.target.value })}
                  placeholder="Type performer name..."
                  className="w-full pl-9 pr-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={16} className="text-green-400" />
              <span className="text-xs text-zinc-400 uppercase tracking-wide">Approved</span>
            </div>
            <p className="text-2xl font-bold">{consents.filter(c => c.status === 'approved').length}</p>
          </div>
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={16} className="text-yellow-400" />
              <span className="text-xs text-zinc-400 uppercase tracking-wide">Pending</span>
            </div>
            <p className="text-2xl font-bold">{consents.filter(c => c.status === 'pending').length}</p>
          </div>
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={16} className="text-orange-400" />
              <span className="text-xs text-zinc-400 uppercase tracking-wide">Warnings</span>
            </div>
            <p className="text-2xl font-bold">{warnings.length}</p>
          </div>
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Scale size={16} className="text-purple-400" />
              <span className="text-xs text-zinc-400 uppercase tracking-wide">Total Records</span>
            </div>
            <p className="text-2xl font-bold">{consents.length}</p>
          </div>
        </div>

        {/* Consent Records Table */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-zinc-800">
            <h2 className="text-lg font-semibold">Faphouse Consent Records</h2>
            <p className="text-sm text-zinc-400 mt-1">
              {filteredConsents.length} of {consents.length} records
            </p>
          </div>
          
          {filteredConsents.length === 0 ? (
            <div className="p-8 text-center text-zinc-500">
              <Scale size={48} className="mx-auto mb-3 opacity-50" />
              <p>No consent records found matching filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-800/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Performer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Platform</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {filteredConsents.map((consent, idx) => (
                    <tr key={idx} className="hover:bg-zinc-800/50 transition">
                      <td className="px-4 py-3 text-sm font-medium">{consent.performer_name || 'Unknown'}</td>
                      <td className="px-4 py-3 text-sm text-zinc-400">Faphouse</td>
                      <td className="px-4 py-3">{getStatusBadge(consent.status || 'pending')}</td>
                      <td className="px-4 py-3 text-sm text-zinc-400">
                        {consent.created_at ? new Date(consent.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to="/crm/faphouse"
                          className="inline-flex items-center gap-1 text-sm text-purple-400 hover:text-purple-300"
                        >
                          View Details
                          <ExternalLink size={12} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 2257 Documents Section (Placeholder) */}
        <div className="mt-8 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-zinc-800">
            <h2 className="text-lg font-semibold">2257 Compliance Documents</h2>
            <p className="text-sm text-zinc-400 mt-1">
              Coming in Phase 2
            </p>
          </div>
          <div className="p-8 text-center text-zinc-500">
            <Scale size={48} className="mx-auto mb-3 opacity-50" />
            <p className="mb-2">2257 document management will be added in the next phase</p>
            <p className="text-xs">Track age verification documents, model releases, and regulatory compliance</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceDashboard;
